import uniq from 'lodash/uniq';
import { isEqual } from 'vet-bones/utils';

import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { DatabaseProperty } from 'src/constants/databaseProperties';
import {
  CreationType,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { Database } from 'src/store/state/databases';
import * as copy from 'src/templates/copy';

const getUpdatedSpgs = (
  spgInfo: SensitivePropertyGroupsInfo,
  databaseSpgs?: Record<string, string[]>
) => {
  const updatedSpgs: Record<string, string[]> = { ...databaseSpgs };
  Object.values(spgInfo).forEach(({ actionType, attributes, publishName }) => {
    let attributesToSet = attributes;
    const existingPropertiesForGroup: string[] | undefined =
      updatedSpgs[publishName];

    if (actionType === CreationType.APPEND) {
      if (existingPropertiesForGroup) {
        attributesToSet = uniq([...existingPropertiesForGroup, ...attributes]);
      }
    } else if (actionType === CreationType.NEW) {
      if (existingPropertiesForGroup) {
        attributesToSet = existingPropertiesForGroup;
      }
    }

    updatedSpgs[publishName] = attributesToSet;
  });
  return updatedSpgs;
};

const formatSpgs = (sensitivePropertiesByGroup: Record<string, string[]>) => {
  const pairs = Object.entries(sensitivePropertiesByGroup).flatMap(
    ([group, properties]) => {
      return properties.map((property) => `${group}=${property}`);
    }
  );
  return pairs;
};

export const publishSensitivePropertyGroups = async (
  database: Database,
  spgInfo: SensitivePropertyGroupsInfo
): Promise<PublishProjectResponse | null> => {
  if (!Object.keys(spgInfo).length) {
    return null;
  }

  const updatedSpgs = getUpdatedSpgs(spgInfo, database.sensitivePropertyGroups);
  if (isEqual(updatedSpgs, database.sensitivePropertyGroups)) {
    return null;
  }

  const updateDbDetailsResponse =
    await databasesStardogRequestDispatchers.updateDatabaseDetails(
      database.id,
      { [DatabaseProperty.SENSITIVE_PROPERTY_GROUPS]: formatSpgs(updatedSpgs) }
    );

  if (
    updateDbDetailsResponse.type ===
    DatabasesActionType.UPDATE_DATABASE_DETAILS_FAILURE
  ) {
    return {
      success: false,
      action: updateDbDetailsResponse,
      actionDescription: copy.notifications.database.publishSpgs,
      message:
        copy.notifications.publishProject.sensitivePropertyGroups.failure,
    };
  }

  return {
    success: true,
    action: updateDbDetailsResponse,
    message: copy.notifications.publishProject.sensitivePropertyGroups.success,
  };
};
